import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Container, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import Address from "./AddressComponent";
import {AppData, Trade} from "./fetchAppData";
import { formErrors, formLabelTranslations, tbo } from "./translation";
import FormValues from "./FormValues";
import ImageUploadComponent from "./ImageUploadComponent";
import { clear, get } from "idb-keyval";
import {COUNTRIES, SearchAndSelect} from "./SearchAndSelect";
import axios from "axios";
import email from "./email";
import logo from './vi2vi_logo.png'

interface Props {
  appData: AppData;
}

enum ReturnReason {
  HARDWARE_ERROR = "hardware_error",
  SOFTWARE_ERROR = "software_error",
  VANDALISM = "vandalism",
  WATERDAMAGE = "waterdamage",
  TRANSPORTATION_DAMAGE = "transportation_damage",
  OTHER_ERROR = "other_error"
}

const RMAFormular: React.FC<Props> = ({ appData }) => {

  // Neuer State für den Loading-Status
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetImagesTrigger, setResetImagesTrigger] = useState(0);

  useEffect(() => {
    // Copy/Paste verhindern
    const handleCopyPaste = (e: any) => e.preventDefault();
    document.addEventListener('copy', handleCopyPaste);
    document.addEventListener('cut', handleCopyPaste);
    document.addEventListener('paste', handleCopyPaste);

    // Rechtsklick blockieren
    const handleContextMenu = (e: any) => e.preventDefault();
    window.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('copy', handleCopyPaste);
      document.removeEventListener('cut', handleCopyPaste);
      document.removeEventListener('paste', handleCopyPaste);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  const persistedLanguage = localStorage.getItem('language');

  const [language, setLanguage] = useState<string>(persistedLanguage ?? 'en');

  const validationSchema = Yup.object({
    land: Yup.string().required(tbo(language, formErrors.requiredField)),
    category: Yup.string().required(tbo(language, formErrors.requiredField)),
    filialnummer: Yup.string()
      .matches(/^\d+$/, tbo(language, {
        de: 'Die Filialnummer muss eine Zahl sein.',
        en: 'The branch number must be a number.',
        fr: 'Le numéro de succursale doit être un nombre.'
      }))
      .required(tbo(language, formErrors.requiredField)),
    filialname: Yup.string().required(tbo(language, formErrors.requiredField)),
    serialNumber: Yup.string()
      .test(
        "serialNumberByTrade",
        tbo(language, {
          de: "Seriennummer darf nur Buchstaben, Zahlen und Bindestriche enthalten.",
          en: "The serial number may contain only letters, numbers, and hyphens.",
          fr: "Le numéro de série ne peut contenir que des lettres, des chiffres et des tirets."
        }),
        function (value = "") {
          const { trade } = this.parent;
          if (!value) return false; // required

          if (trade === Trade.INSTA_COMMUNICATION) {
            // multiple, separated by commas
            const splitted = value.split(",").map((s: string) => s.trim()).filter((s: string) => s.length > 0);
            return splitted.every((s: string) => /^[A-Z0-9-]+$/.test(s));
          } else {
            // single
            return /^[A-Z0-9-]+$/.test(value);
          }
        }
      )
      .required(tbo(language, formErrors.requiredField)),
    trade: Yup.string().required(tbo(language, formErrors.requiredField)),
    producer: Yup.string().when("trade", (trade, schema) => {
      return trade && trade.length > 0
        ? schema.required(tbo(language, formErrors.requiredField))
        : schema.notRequired();
    }),
    reason: Yup.string().required(tbo(language, formErrors.requiredField)),
    defectDescription: Yup
      .string()
      .matches(
        /^(?!.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{3,}).*$/,
        tbo(language, {
          de: "Vermeide zu viele Sonderzeichen hintereinander",
          en: "Avoid too many special characters in a row",
          fr: "Évite trop de caractères spéciaux à la suite"
        })
      )
      .matches(
        /^(?!.*\s{3,}).*$/,
        tbo(language, {
          "de": "Vermeide mehrere Leerzeichen hintereinander",
          "en": "Avoid multiple consecutive spaces",
          "fr": "Évite plusieurs espaces consécutifs"
        })
      )
      .min(20, tbo(language, formErrors.defectDescriptionAtLeast20Chars))
      .required(tbo(language, formErrors.requiredField)),
    errorDescription: Yup.string()
      .test('required-if-trade-producer', tbo(language, formErrors.requiredField), function(value) {
        const { trade, producer } = this.parent;
        // only check required if trade, producer have values
        if (trade && producer && trade !== '' && producer !== '') {
          return value !== '';
        }
        return true;
      })
      .nullable(),
    serialNumberErrichterPaket: Yup
      .string()
      .when('erichterpaket', {
        is: 'yes',
        then: (schema) => schema.required(tbo(language, formErrors.serialNumberErrichterPaket)),
        otherwise: (schema) => schema.notRequired(),
      }),
    images: Yup.array()
    .max(3, tbo(language, {
      de: "Maximal 3 Bilder erlaubt.",
      en: "Maximum 3 images allowed.",
      fr: "Maximum 3 images autorisées.",
    }))
    .when("reason", {
      is: ReturnReason.TRANSPORTATION_DAMAGE || ReturnReason.VANDALISM,
      then: (schema) =>
        schema.min(1, tbo(language, {
          de: "Es muss mindestens ein Bild hochgeladen werden.",
          en: "At least one image must be uploaded.",
          fr: "Au moins une image doit être téléchargée.",
        })),
      otherwise: (schema) => schema,
    }),
    deliveryAddress: Yup.object({
      companyName: Yup.string().required(tbo(language, formErrors.requiredField)),
      streetNumber: Yup.string().required(tbo(language, formErrors.requiredField)),
      postalCode: Yup.string().required(tbo(language, formErrors.requiredField)),
      city: Yup.string().required(tbo(language, formErrors.requiredField)),
      contactPerson: Yup.string().required(tbo(language, formErrors.requiredField)),
      phone: Yup.string().required(tbo(language, formErrors.requiredField)),
      email: Yup
          .string()
          .matches(/^[^\s@]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/, tbo(language, formErrors.invalidEmail))
          .required(tbo(language, formErrors.requiredField))
    }),
    pickupAddress: Yup.object().when("deliveryAndPickUpAdressSame", (deliveryAndPickUpAdressSame, schema) => {
      if (deliveryAndPickUpAdressSame[0] === "no") {
        return schema.shape({
          companyName: Yup.string().required(tbo(language, formErrors.requiredField)),
          streetNumber: Yup.string().required(tbo(language, formErrors.requiredField)),
          postalCode: Yup.string().required(tbo(language, formErrors.requiredField)),
          city: Yup.string().required(tbo(language, formErrors.requiredField)),
          contactPerson: Yup.string().required(tbo(language, formErrors.requiredField)),
          phone: Yup.string().required(tbo(language, formErrors.requiredField)),
          email: Yup
              .string()
              .matches(/^[^\s@]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/, tbo(language, formErrors.invalidEmail))
              .required(tbo(language, formErrors.requiredField))
        });
      } else {
        return schema;
      }
    }),
  });

  const readFieldFromLocalStorage = (fieldName: string): string => {
    const data = localStorage.getItem(fieldName);
    if (!data) {
      return ""
    }
    return JSON.parse(data);    
  }

  const initialValues : FormValues = {
    land: readFieldFromLocalStorage('land'),
    filialnummer: readFieldFromLocalStorage('filialnummer'),
    filialname: readFieldFromLocalStorage('filialname'),
    serialNumber: readFieldFromLocalStorage('serialNumber'),
    serialNumberErrichterPaket: readFieldFromLocalStorage('serialNumberErrichterPaket'),
    trade: readFieldFromLocalStorage('trade'),
    category: readFieldFromLocalStorage('category'),
    producer: readFieldFromLocalStorage('producer'),
    reason: readFieldFromLocalStorage('reason'),
    defectDescription: readFieldFromLocalStorage('defectDescription'),
    deliveryAddress: {
      companyName: readFieldFromLocalStorage('deliveryAddress_companyName'),
      streetNumber: readFieldFromLocalStorage('deliveryAddress_streetNumber'),
      postalCode: readFieldFromLocalStorage('deliveryAddress_postalCode'),
      city: readFieldFromLocalStorage('deliveryAddress_city'),
      contactPerson: readFieldFromLocalStorage('deliveryAddress_contactPerson'),
      phone: readFieldFromLocalStorage('deliveryAddress_phone'),
      email: readFieldFromLocalStorage('deliveryAddress_email')
    },
    deliveryAndPickUpAdressSame: readFieldFromLocalStorage('deliveryAndPickUpAdressSame') ?? "no",
    pickupAddress: {
      companyName: readFieldFromLocalStorage('pickupAddress_companyName'),
      streetNumber: readFieldFromLocalStorage('pickupAddress_streetNumber'),
      postalCode: readFieldFromLocalStorage('pickupAddress_postalCode'),
      city: readFieldFromLocalStorage('pickupAddress_city'),
      contactPerson: readFieldFromLocalStorage('pickupAddress_contactPerson'),
      phone: readFieldFromLocalStorage('pickupAddress_phone'),
      email: readFieldFromLocalStorage('pickupAddress_email')
    },
  }

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema,
    onSubmit: async (values) => {
      if (isSubmitting) return; // Doppelklicks verhindern

      if (window.confirm(tbo(language, {
        de: 'Möchten Sie das Fomular wirklich absenden?',
        en: 'Do you really want to submit the form?',
        fr: 'Voulez-vous vraiment soumettre le formulaire ?'        
      }))) {

        setIsSubmitting(true); // Loading starten

        try {
          const emailContent = await email(values, appData);

          const fileToBase64 = (file: File): Promise<string> => {
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => {
                const dataUrl = reader.result as string;
                // Entferne den "data:image/jpeg;base64," Teil
                const base64 = dataUrl.split(',')[1];
                resolve(base64);
              };
              reader.readAsDataURL(file);
            });
          };

          const fetchedProducer = appData.hersteller.find((h) => h.id.toString() === values.producer.toString());
          const customValues = {
            producer: fetchedProducer ? fetchedProducer.name : "",
            erichterpaket: values.erichterpaket === "yes" ? true : false,
          }

          const mergedValues = {...values, ...customValues};
          console.log(mergedValues);

          const storedImages = await get("storedImages") || [];
          const imagesBase64 = await Promise.all(
              storedImages.map(async (file: File) => ({
                filename: file.name,
                data: await fileToBase64(file)
              }))
          );
          await axios
              .post("https://hook.eu2.make.com/pyuq1dbk2awrbvlb6kyssp8ske1obj33", {
                rawValues: mergedValues,
                email: emailContent,
                images: imagesBase64,
                environment: process.env.REACT_APP_ENV || 'production'
              });

          // clear form
          formik.resetForm();
          localStorage.clear();
          await clear();
          setResetImagesTrigger(v => v + 1);
          console.log('cleared local storage');

          alert(tbo(language, {
            de: "Formular wurde erfolgreich übermittelt!",
            en: "Form submitted successfully!",
            fr: "Formulaire soumis avec succès!"
          }));

        } catch (e) {
          console.error(e);
          alert(tbo(language, {
            de: "Fehler beim Übermitteln des Formulars!",
            en: "Error submitting form!",
            fr: "Erreur lors de la soumission du formulaire!"
          }));
        } finally {
          setIsSubmitting(false); // Loading beenden
        }
      }
    },
  });

  const onChange = (event: React.ChangeEvent<any>) : void => {
    const { name, value } = event.target;
    localStorage.setItem(name, JSON.stringify(value));
    formik.handleChange(event)
  };

  // Spezielle onChange-Funktion für das Land-Feld
  const onCountryChange = (value: string) => {
    localStorage.setItem('land', JSON.stringify(value));
    formik.setFieldValue('land', value);
  };

  const previousTradeRef = React.useRef<string | null>(null);
  useEffect(() => {
    // Nur zurücksetzen, wenn sich der Trade wirklich ändert
    if (previousTradeRef.current !== null && previousTradeRef.current !== formik.values.trade) {
      formik.setFieldValue("producer", "");
      formik.setFieldValue("category", "");
      formik.setFieldValue("errorDescription", "");
    }
    previousTradeRef.current = formik.values.trade;
  }, [formik.values.trade]);

  const previousProducerRef = React.useRef<string | null>(null);
  useEffect(() => {
    // Kategorie zurücksetzen, wenn sich der Hersteller ändert
    if (previousProducerRef.current !== null && previousProducerRef.current !== formik.values.producer) {
      formik.setFieldValue("category", "");
    }
    previousProducerRef.current = formik.values.producer;
  }, [formik.values.producer]);

  return (
    <Container>
      <Row className="align-items-center mb-4">
        <div className="d-flex align-items-center gap-3 py-3">
          <img
              src={logo}
              alt="Logo"
              style={{
                height: 64,          // größer wie Header-Logo
                width: "auto",
                display: "block"
              }}
          />
          <h1 className={"text-color"} style={{ margin: 0, lineHeight: 1.1 }}>
            {tbo(language, formLabelTranslations.formHeading)}
          </h1>
        </div>
      </Row>

      <Row>
        <Form.Group as={Row} className="mb-3" controlId="formPlaintextEmail">
          <Form.Label column sm="2">
            Choose Language (🇬🇧): 
          </Form.Label>
          <Col sm="3">
            <Form.Select 
              value={language}
              onChange={(e) => {
                localStorage.setItem('language', e.target.value)
                setLanguage(e.target.value)
              }} 
            >
              <option value="de">Deutsch</option>
              <option value="en">English</option>
              <option value="fr">French</option>
            </Form.Select>
          </Col>
        </Form.Group> 
      </Row>

      <Form autoComplete={"off"} noValidate onSubmit={(e) => {
        formik.handleSubmit(e)
      }}>
        <Row className="mb-5">
          <Col md={6}>
            <Form.Group className="mb-1">
              <Form.Label>{tbo(language, formLabelTranslations.land)}</Form.Label>
              <SearchAndSelect
                  value={formik.values.land}
                  onChange={onCountryChange}
                  onBlur={() => formik.setFieldTouched('land', true)}
                  isInvalid={formik.touched.land && Boolean(formik.errors.land)}
                  placeholder={tbo(language, formLabelTranslations.formPleaseChoose)}
                  errorMessage={formik.errors.land as string}
                  options={COUNTRIES}
              />
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Label>{tbo(language, formLabelTranslations.filialnummer)}</Form.Label>
              <Form.Control
                name="filialnummer"
                type="number"
                value={formik.values.filialnummer}
                onChange={onChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  formik.touched.filialnummer &&
                  Boolean(formik.errors.filialnummer)
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.filialnummer}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Label>{tbo(language, formLabelTranslations.filialname)}</Form.Label>
              <Form.Control
                name="filialname"
                type="text"
                value={formik.values.filialname}
                onChange={onChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  formik.touched.filialname && Boolean(formik.errors.filialname)
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.filialname}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Label>{tbo(language, formLabelTranslations.serialNumber)}</Form.Label>
              {/* Translation */}
              {formik.values.trade === Trade.INSTA_COMMUNICATION && (
                <div style={{ fontSize: "0.9em", marginBottom: "4px" }}>
                  Mehrere Seriennummern mit Komma trennen.
                </div>
              )}
              <Form.Control
                as={'textarea'}
                name="serialNumber"
                type="text"
                value={formik.values.serialNumber}
                onChange={(e) => {
                  const newValue = e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9-,]/g, "");
                  formik.setFieldValue("serialNumber", newValue);
                  localStorage.setItem("serialNumber", JSON.stringify(newValue));
                }}
                onBlur={formik.handleBlur}
                isInvalid={
                  formik.touched.serialNumber &&
                  Boolean(formik.errors.serialNumber)
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.serialNumber}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Check
                type="checkbox"
                label={tbo(language, formLabelTranslations.erichterpaket)}
                name="erichterpaket"
                checked={formik.values.erichterpaket === "yes"}
                onChange={(e) => {
                  const value = e.target.checked ? "yes" : "no";
                  formik.setFieldValue("erichterpaket", value);
                  localStorage.setItem("erichterpaket", JSON.stringify(value));
                }}
                isInvalid={
                  formik.touched.erichterpaket &&
                  Boolean(formik.errors.erichterpaket)
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.erichterpaket}
              </Form.Control.Feedback>
            </Form.Group>

            {
              (() => {
                if (formik.values.erichterpaket === "yes") {
                  return (
                    <Form.Group className="mb-1">
                      <Form.Label>{tbo(language, formLabelTranslations.seriennummerErrichterpaket)}</Form.Label>
                      {/* Translation */}
                      <Form.Control
                        name="serialNumberErrichterPaket"
                        type="text"
                        value={formik.values.serialNumberErrichterPaket}
                        onChange={(e) => {
                          const newValue = e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9-,]/g, "");
                          formik.setFieldValue("serialNumberErrichterPaket", newValue);
                          localStorage.setItem("serialNumberErrichterPaket", JSON.stringify(newValue));
                        }}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          formik.touched.serialNumberErrichterPaket &&
                          Boolean(formik.errors.serialNumberErrichterPaket)
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.serialNumberErrichterPaket}
                      </Form.Control.Feedback>
                    </Form.Group>
                  )
                }
              })()
            }
            
            <Form.Group className="mb-1">
              <Form.Label>{tbo(language, formLabelTranslations.trade)}</Form.Label>
              <Form.Select
                name="trade"
                value={formik.values.trade}
                onChange={onChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.trade && Boolean(formik.errors.trade)}
              >
                <option value="">{tbo(language, formLabelTranslations.formPleaseChoose)}</option>
                <option value={Trade.VIDEO}>Video</option>
                <option value={Trade.INSTA_COMMUNICATION}>In Store Communication (Audio)</option>
                <option value={Trade.HEADSET}>Headset</option>
                <option value={Trade.EMA}>EMA</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formik.errors.trade}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Label>{tbo(language, formLabelTranslations.producer)}</Form.Label>
              <SearchAndSelect
                  value={formik.values.producer}
                  onChange={(value) => {
                    localStorage.setItem('producer', JSON.stringify(value));
                    formik.setFieldValue('producer', value);
                  }}
                  onBlur={() => formik.setFieldTouched('producer', true)}
                  isInvalid={formik.touched.producer && Boolean(formik.errors.producer)}
                  placeholder={tbo(language, formLabelTranslations.formPleaseChoose)}
                  errorMessage={formik.errors.producer as string}
                  options={appData.hersteller.map((p) => ({
                    value: `${p.id}`,
                    label: p.name
                  }))}
              />
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Label>{tbo(language, formLabelTranslations.category)}</Form.Label>
              <Form.Select
                  name="category"
                  value={formik.values.category}
                  onChange={onChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                      formik.touched.category && Boolean(formik.errors.category)
                  }
              >
                <option value="">{tbo(language, formLabelTranslations.formPleaseChoose)}</option>
                {appData.produktKategorie.map((category) => (
                    <option key={`category-${category.id}`} value={`${category.id}`}>
                      {category.name}
                    </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formik.errors.category as string}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{tbo(language, formLabelTranslations.reason)}</Form.Label>
              <Form.Select
                name="reason"
                value={formik.values.reason}
                onChange={onChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.reason && Boolean(formik.errors.reason)}
              >
                <option value="">{tbo(language, formLabelTranslations.formPleaseChoose)}</option>
                <option value={ReturnReason.HARDWARE_ERROR}>{tbo(language, formLabelTranslations.reasonHardwareError)}</option>
                <option value={ReturnReason.SOFTWARE_ERROR}>{tbo(language, formLabelTranslations.reasonSoftwareError)}</option>
                <option value={ReturnReason.VANDALISM}>{tbo(language, formLabelTranslations.reasonVandalism)}</option>
                <option value={ReturnReason.WATERDAMAGE}>{tbo(language, formLabelTranslations.reasonWaterdamage)}</option>
                <option value={ReturnReason.TRANSPORTATION_DAMAGE}>{tbo(language, formLabelTranslations.reasonTransportdamage)}</option>
                <option value={ReturnReason.OTHER_ERROR}>{tbo(language, formLabelTranslations.reasonOther)}</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formik.errors.reason as string}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{tbo(language, formLabelTranslations.defectDescription)}</Form.Label>
              <Form.Control
                as={'textarea'}
                name="defectDescription"
                value={formik.values.defectDescription}
                onChange={onChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.defectDescription && Boolean(formik.errors.defectDescription)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.defectDescription as string}
              </Form.Control.Feedback>
            </Form.Group>
            <ImageUploadComponent formik={formik} language={language} resetTrigger={resetImagesTrigger} />
          </Col>
          <Col md={6}>
            <div className="mb-4">
              <h5 className={"text-color"}>{tbo(language, formLabelTranslations.deliveryAddress)}</h5>
              <Address language={language} prefix="deliveryAddress" formik={formik} />
            </div>
            <Form.Group className="mb-4">
              <Form.Check
                type="checkbox"
                label={tbo(language, formLabelTranslations.deliveryAndPickUpAdressSame)}
                name="deliveryAndPickUpAdressSame"
                checked={formik.values.deliveryAndPickUpAdressSame === "no"}
                onChange={(e) => {
                  const value = e.target.checked ? "no" : "yes";
                  formik.setFieldValue("deliveryAndPickUpAdressSame", value);
                  localStorage.setItem("deliveryAndPickUpAdressSame", JSON.stringify(value));
                }}
                isInvalid={
                  formik.touched.deliveryAndPickUpAdressSame &&
                  Boolean(formik.errors.deliveryAndPickUpAdressSame)
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.deliveryAndPickUpAdressSame}
              </Form.Control.Feedback>
            </Form.Group>
            {(() => {
              if (formik.values.deliveryAndPickUpAdressSame === "no") {
                return (
                    <div>
                      <h5 className={"text-color"}>{tbo(language, formLabelTranslations.headingDeliveryAndPickUpAdressSame)}</h5>
                      <Address language={language} prefix="pickupAddress" formik={formik} />
                    </div>
                )
              }
            })()}

            <Button 
              variant="primary" 
              className="me-2 btn-orange"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {tbo(language, {
                    de: "Wird gesendet...",
                    en: "Sending...",
                    fr: "Envoi en cours..."
                  })}
                </>
              ) : (
                tbo(language, formLabelTranslations.formSend)
              )}
            </Button>
            <Button 
              variant="danger" 
              disabled={isSubmitting}
              className={"btn-orange"}
              onClick={(e) => {
                e.preventDefault();
                if (window.confirm(tbo(language, formLabelTranslations.formDeleteMessage))) {
                  (async () => {
                    formik.resetForm();
                    localStorage.clear();
                    await clear()
                  })()
                      .catch(() => console.error("Error clearing local storage"))
                      .then(() => window.location.reload())
                }
              }}
            >
              {tbo(language, formLabelTranslations.formDelete)}
            </Button>

          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default RMAFormular;